/** @jsxImportSource @emotion/react */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import { Button } from '@mui/material';
import { CellType, DataMap } from '@grapecity/wijmo.grid';
import { SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import CustomDialog from 'components/modals/common/CustomDialog';
import CustomGrid from 'components/grids/CustomGrid';
import { simpleRequest } from 'apis/ip/IpCheckPlan';
import { useMessageBar } from 'hooks/useMessageBar';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { Code } from 'models/common/CommonCode';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import WJCellTextarea from 'components/inputs/WJCellTextarea';

type Props = {
  open: boolean;
  close: () => void;
  params: any;
};

export const IpCheckResultPointModal = ({ open, close, params }: Props) => {
  const { t } = useTranslation();
  const gridRef = useRef<any>();
  const { openMessageBar } = useMessageBar();
  const [rowData, setRowData] = useState<any>([]);
  const [code, setCode] = useState<any>({});
  const [isOpenCellTextarea, setOpenCellTextarea] = useState<boolean>(false);
  const [hitTest, setHitTest] = useState<any>();

  useEffect(() => {
    getCommonCodes();
  }, []);

  const getCommonCodes = async () => {
    const meterType: Code[] = await getCommonCodeNames('METER_TYPE');
    const ipUom: Code[] = await getCommonCodeNames('IP_UOM');

    setCode({
      meterType: meterType,
      ipUom: ipUom,
    });
  };

  useEffect(() => {
    drawList(params);
  }, []);

  const drawList = async (params: any) => {
    await simpleRequest(params, `/v1/selectFromIpCheckParaPoint`).then((result: any[]) => {
      setRowData(result);
    });
  };

  const saveBtnOnClick = async () => {
    const params = {
      listIpCheckParaPoint: rowData,
    };

    await simpleRequest(params, `/v1/updateToIpCheckParaPoint`).then((result: any[]) => {
      if (!result) {
        openMessageBar({
          type: 'error',
          content:
            '그리드 내용 ' +
            t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
        });
        return;
      }
      openMessageBar({
        type: 'confirm',
        content: t('com.label.저장되었습니다.', '저장되었습니다.'),
      });
      return result;
    });
  };

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;

      if ('rmk' === binding) {
        cell.ariaReadOnly = false;
      }
    }
  });

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      const binding = grid.columns[ht.col].binding;
      const item = grid.rows[ht.row].dataItem;

      if (ht.panel === grid.cells) {
        setHitTest(ht);
        // 주석
        if ('rmk' === binding) {
          setOpenCellTextarea(true);
        }
      }
    });
  };

  const layoutDefinition = useMemo(
    () => [
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        width: 43,
        isReadOnly: true,
        cellTemplate: GridStatusCellTemplate,
        visible: false,
      },
      {
        binding: 'paraId',
        header: String(t('ip.grid.파라미터ID', '파라미터ID')),
        width: 130,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'paraName',
        header: String(t('ip.grid.파라미터명', '파라미터명')),
        width: 150,
        isReadOnly: true,
      },
      {
        binding: 'paraPointNo',
        header: String(t('ip.grid.포인터 번호', '포인터 번호')),
        width: 105,
        isReadOnly: true,
      },
      {
        binding: 'paraPointName',
        header: String(t('ip.grid.측정개소명', '측정개소명')),
        width: 165,
        isReadOnly: true,
      },
      {
        binding: 'paraPointDesc',
        header: String(t('ip.grid.측정개소 설명', '측정개소 설명')),
        minWidth: 93,
        width: '*',
        isReadOnly: true,
      },
      {
        binding: 'meterType',
        header: String(t('ip.grid.미터유형', '미터유형')),
        width: 88,
        align: 'center',
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.meterType || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'lowerBound',
        header: String(t('ip.grid.하한', '하한')),
        width: 63,
        align: 'right',
        isRequired: false,
      },
      {
        binding: 'upperBound',
        header: String(t('ip.grid.상한', '상한')),
        width: 63,
        align: 'right',
        isRequired: false,
      },
      {
        binding: 'uom',
        header: String(t('ip.grid.UOM', 'UOM')),
        width: 85,
        align: 'center',
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.ipUom || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'rmk',
        header: String(t('ip.grid.주석', '주석')),
        width: 100,
        align: 'left',
        isReadOnly: true,
        isRequired: false,
      },
    ],
    [code]
  );

  const dialogButtons = [
    <Button
      key="confirm"
      css={IconButton.button}
      className="confirm"
      onClick={saveBtnOnClick}
      disableRipple
    >
      {t('com.button.저장', '저장')}
    </Button>,
  ];

  return (
    <CustomDialog
      open={open}
      title={t('ip.label.측정개소 대상 조회', '측정개소 대상 조회')}
      onClose={close}
      onCancel={close}
      buttons={dialogButtons}
    >
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('ip.label.측정개소 대상 계획', '측정개소 대상 계획')}</h3>
          <span className="total">
            {t('com.label.총', '총')} <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={300}
        isSelector={false}
        allowPinning={false}
        excludeFilter={['paraId', 'paraName']}
        initialized={onInitialized}
      />
      {isOpenCellTextarea && (
        <WJCellTextarea
          grid={gridRef.current}
          hitTest={hitTest}
          close={() => setOpenCellTextarea(false)}
        />
      )}
    </CustomDialog>
  );
};
